import { X, CheckCircle, XCircle, FileText, Eye } from 'lucide-react';
import { useState } from 'react';
import { useUpdateApprovalStatus } from '../hooks/useUpdateApprovalStatus';

export const ViewPropertyModal = (props) => {
  const { isOpen, onClose, property } = props;
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [openedDocs, setOpenedDocs] = useState(new Set());
  const { mutate: updateApprovalStatus, isPending: isUpdating } = useUpdateApprovalStatus();

  const isImage = (url) =>
  /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url);

  if (!isOpen || !property) return null;

  const handleApprove = () => {
  updateApprovalStatus(
    {
      propertyId: property._id,
      approvalStatus: 'approved',
    },
    {
      onSuccess: () => {
        onClose();
      },
    }
  );
};

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      return;
    }
    updateApprovalStatus(
      {
        propertyId: property._id,
        approvalStatus: 'rejected',
        rejectionReason: rejectionReason.trim(),
      },
      {
        onSuccess: () => {
          setShowRejectInput(false);
          setRejectionReason('');
          onClose();

        },
      }
    );
  };

  const isApprovalPending = property.approvalStatus === 'pending';

  const totalDocs = property.ownershipDocuments?.length ?? 0;
const allDocsOpened = totalDocs === 0 || openedDocs.size >= totalDocs;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-foreground">
            Property Detailss
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Images Gallery */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">
              Property Photos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {property.photos?.map((photo, index) => (
                <div key={index} className="relative">
                  <img
                    src={photo.url}
                    alt={photo.caption || `Property ${index + 1}`}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  {photo.isPrimary && (
                    <span className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                      Primary
                    </span>
                  )}
                  {/* {photo.caption && (
                    <p className='text-xs text-muted-foreground mt-1'>{photo.caption}</p>
                  )} */}
                </div>
              ))}
            </div>
          </div>

          {/* Ownership Documents */}
{property.ownershipDocuments?.length > 0 && (
  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
    <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">
      Ownership Documents
    </h3>

    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {property.ownershipDocuments.map((doc, index) => (
     <div
  key={index}
  className="border rounded-lg overflow-hidden bg-background"
>
  <a
    href={doc.url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => setOpenedDocs(prev => new Set(prev).add(doc.url))}
    className="group relative block"
  >
    {isImage(doc.url) ? (
      <>
        <img
          src={doc.url}
          alt={doc.title}
          className="w-full h-40 object-cover"
        />

        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Eye className="text-white" size={32} />
        </div>
      </>
    ) : (
      <>
        <div className="h-40 flex items-center justify-center bg-muted">
          <FileText size={40} />
        </div>

        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Eye className="text-white" size={32} />
        </div>
      </>
    )}
  </a>

  <div className="p-3">
    <p className="text-sm font-medium truncate">
      {doc.title || `Document ${index + 1}`}
    </p>

    <p className="text-xs text-muted-foreground mt-1">
      Uploaded on {new Date(doc.uploadedAt).toLocaleDateString()}
    </p>
  </div>
</div>
      ))}
    </div>
  </div>
)}

          {/* Basic Information */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">
                  Property Location
                </p>
                <p className="font-medium text-foreground">
                  {property.propertyLocation}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Property Category
                </p>
                <p className="font-medium text-foreground capitalize">
                  {property.propertyCategory}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Property Sub-Category
                </p>
                <p className="font-medium text-foreground capitalize">
                  {property.subcategoryId?.name?.english}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Property Type</p>
                <p className="font-medium text-foreground">
                  {property.propertyType?.replace("_", " ")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Price</p>
                <p className="font-medium text-foreground text-lg">
                  ₹{property.propertyPrice?.toLocaleString()}
                </p>
              </div>
              {property.additionalDetails && (
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground">
                    Additional Details
                  </p>
                  <p className="font-medium text-foreground">
                    {property.additionalDetails}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Property Details */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">
              Property Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Property Area</p>
                <p className="font-medium text-foreground">
                  
                  {property.propertyArea
                    ? `${property.propertyArea} sq ft`
                    : "N/A"}
                  
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bedrooms</p>
                <p className="font-medium text-foreground">
                  {property.bedrooms || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Balcony</p>
                <p className="font-medium text-foreground">
                  {property.balcony || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Furnishing</p>
                <p className="font-medium text-foreground capitalize">
                  {property.furnishing?.replace("_", " ")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Property Floor
                </p>
                <p className="font-medium text-foreground">
                  {property.totalPropertyFloor || "N/A"}
                </p>
              </div>
              {property.flatNumber && (
                <div>
                  <p className="text-sm text-muted-foreground">Flat Number</p>
                  <p className="font-medium text-foreground">
                    {property.flatNumber}
                  </p>
                </div>
              )}
              {property.numberOfFlats && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Number of Flats
                  </p>
                  <p className="font-medium text-foreground">
                    {property.numberOfFlats}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">
                  Construction Status
                </p>
                <p className="font-medium text-foreground">
                  {property.constructionStatus
                    ?.replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </p>
              </div>
              {/* {property.totalConstructionFloor && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Construction Floor
                  </p>
                  <p className="font-medium text-foreground">
                    {property.totalConstructionFloor}
                  </p>
                </div>
              )} */}
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-4">
              Address
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <p className="text-sm text-muted-foreground">Address Line</p>
                <p className="font-medium text-foreground">
                  {property.address?.addressLine}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">City</p>
                <p className="font-medium text-foreground">
                  {property.address?.city}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">State</p>
                <p className="font-medium text-foreground">
                  {property.address?.state}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Country</p>
                <p className="font-medium text-foreground">
                  {property.address?.country}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pincode</p>
                <p className="font-medium text-foreground">
                  {property.address?.pincode}
                </p>
              </div>
              {property.address?.location?.coordinates && (
                <div className="md:col-span-2">
                  <p className="text-sm text-muted-foreground">Coordinates</p>
                  <p className="font-medium text-foreground">
                    Lat: {property.address.location.coordinates[1]}, Long:{" "}
                    {property.address.location.coordinates[0]}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Area/Sector/Society */}
          {property.areaSectorSociety && (
            <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-teal-900 dark:text-teal-100 mb-4">
                Area/Sector/Society
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {property.areaSectorSociety.area && (
                  <div>
                    <p className="text-sm text-muted-foreground">Area</p>
                    <p className="font-medium text-foreground">
                      {property.areaSectorSociety.area}
                    </p>
                  </div>
                )}
                {property.areaSectorSociety.sector && (
                  <div>
                    <p className="text-sm text-muted-foreground">Sector</p>
                    <p className="font-medium text-foreground">
                      {property.areaSectorSociety.sector}
                    </p>
                  </div>
                )}
                {property.areaSectorSociety.society && (
                  <div>
                    <p className="text-sm text-muted-foreground">Society</p>
                    <p className="font-medium text-foreground">
                      {property.areaSectorSociety.society}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Rooms */}
          {property.additionalRooms && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-4">
                Additional Rooms
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(property.additionalRooms).map(
                  ([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span
                        className={`w-3 h-3 rounded-full ${value ? "bg-green-500" : "bg-gray-300"}`}
                      ></span>
                      <span className="text-sm capitalize">
                        {key.replace(/([A-Z])/g, " $1").trim()}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Overlooking */}
          {property.overlooking && (
            <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-cyan-900 dark:text-cyan-100 mb-4">
                Overlooking
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(property.overlooking).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full ${value ? "bg-green-500" : "bg-gray-300"}`}
                    ></span>
                    <span className="text-sm capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Amenities */}
          {property.amenities && (
            <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-violet-900 dark:text-violet-100 mb-4">
                Amenities
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(property.amenities).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full ${value ? "bg-green-500" : "bg-gray-300"}`}
                    ></span>
                    <span className="text-sm capitalize">
                      {key.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Owner Information */}
          <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-pink-900 dark:text-pink-100 mb-4">
              Owner Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Owner Type</p>
                <p className="font-medium text-foreground capitalize">
                  {property.ownerType}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Owner Name</p>
                <p className="font-medium text-foreground">
                  {property.ownerName}
                </p>
              </div>
              {property.fatherMotherHusbandWifeName && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Father/Mother/Husband/Wife Name
                  </p>
                  <p className="font-medium text-foreground">
                    {property.fatherMotherHusbandWifeName}
                  </p>
                </div>
              )}
              {property.currentAddress && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Current Address
                  </p>
                  <p className="font-medium text-foreground">
                    {property.currentAddress}
                  </p>
                </div>
              )}
              {property.currentPincode && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    Current Pincode
                  </p>
                  <p className="font-medium text-foreground">
                    {property.currentPincode}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Seller Information */}
          {property.sellerId && (
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4">
                Seller Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium text-foreground">
                    {property.sellerId.firstName} {property.sellerId.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground">
                    {property.sellerId.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium text-foreground">
                    {property.sellerId.phoneNumber}
                  </p>
                </div>
                {/* {property.sellerId.profilePhoto && (
                  <div>
                    <p className='text-sm text-muted-foreground'>Profile Photo</p>
                    <img
                      src={property.sellerId.profilePhoto}
                      alt='Seller'
                      className='w-16 h-16 rounded-full object-cover'
                    />
                  </div>
                )} */}
              </div>
            </div>
          )}

          {/* Status Information */}
          <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p
                  className={`font-medium capitalize ${
                    property.status === "active"
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {property.status?.replace("_", " ")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approval Status</p>
                <p
                  className={`font-medium capitalize ${
                    property.approvalStatus === "approved"
                      ? "text-green-600 dark:text-green-400"
                      : property.approvalStatus === "rejected"
                        ? "text-red-600 dark:text-red-400"
                        : "text-yellow-600 dark:text-yellow-400"
                  }`}
                >
                  {property.approvalStatus}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Verified</p>
                <p
                  className={`font-medium ${
                    property.isVerified
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {property.isVerified ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created At</p>
                <p className="font-medium text-foreground">
                  {new Date(property.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Updated At</p>
                <p className="font-medium text-foreground">
                  {new Date(property.updatedAt).toLocaleString()}
                </p>
              </div>
              {property.verifiedAt && (
                <div>
                  <p className="text-sm text-muted-foreground">Verified At</p>
                  <p className="font-medium text-foreground">
                    {new Date(property.verifiedAt).toLocaleString()}
                  </p>
                </div>
              )}
              {property.rejectionReason && (
                <div className="md:col-span-3">
                  <p className="text-sm text-muted-foreground">
                    Rejection Reason
                  </p>
                  <p className="font-medium text-red-600 dark:text-red-400">
                    {property.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Approval Actions */}
          {isApprovalPending && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4">
                Approval Actions
              </h3>

              {!showRejectInput ? (
                <div className="flex gap-3">
                  <button
                    onClick={handleApprove}
                    disabled={isUpdating || !allDocsOpened}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle size={20} />
                    {isUpdating ? "Approving..." : "Approve Property"}
                  </button>
                  <button
                    onClick={() => setShowRejectInput(true)}
                    disabled={isUpdating || !allDocsOpened}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle size={20} />
                    Reject Property
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a reason for rejecting this property..."
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                      disabled={isUpdating}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleReject}
                      disabled={isUpdating || !rejectionReason.trim()}
                      className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle size={20} />
                      {isUpdating ? "Rejecting..." : "Confirm Rejection"}
                    </button>
                    <button
                      onClick={() => {
                        setShowRejectInput(false);
                        setRejectionReason("");
                      }}
                      disabled={isUpdating}
                      className="px-6 py-2.5 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

